import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DriverSelector from '../../components/driverSelector';
import { Form, Alert, Spinner, Button, Table } from 'react-bootstrap';
import axios from 'axios';

const MonthlyReports = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthsData, setMonthsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [savingInvoices, setSavingInvoices] = useState(false);
  const [existingInvoices, setExistingInvoices] = useState(null);
  const [deletingInvoices, setDeletingInvoices] = useState({});
  const [error, setError] = useState(null);
  const [reportsData, setReportsData] = useState(null);
  const [validationError, setValidationError] = useState(null);

  const months = [
    { value: '01', label: t('january') },
    { value: '02', label: t('february') },
    { value: '03', label: t('march') },
    { value: '04', label: t('april') },
    { value: '05', label: t('may') },
    { value: '06', label: t('june') },
    { value: '07', label: t('july') },
    { value: '08', label: t('august') },
    { value: '09', label: t('september') },
    { value: '10', label: t('october') },
    { value: '11', label: t('november') },
    { value: '12', label: t('december') }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Завантаження даних тижнів з сервера
  const fetchMonthsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get('/api/monthly-reports/months', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setMonthsData(response.data.data);
    } catch (err) {
      console.error('Помилка завантаження даних тижнів:', err);
      setError(t('errorLoadingWeeksData'));
    } finally {
      setLoading(false);
    }
  };

  // Отримання тижнів для обраного місяця та року
  const getWeeksForSelectedMonth = () => {
    if (!monthsData || !selectedMonth || !selectedYear) {
      return [];
    }
    
    const yearData = monthsData[selectedYear.toString()];
    if (!yearData) {
      return [];
    }
    
    return yearData[selectedMonth] || [];
  };

  const deletableInvoiceStatuses = ['draft', 'saved', 'sent', 'published'];

  const isInvoiceDeletable = (status) => {
    if (!status) return false;
    return deletableInvoiceStatuses.includes(status.toLowerCase());
  };

  // Завантаження даних при монтуванні компонента
  useEffect(() => {
    fetchMonthsData();
    
    // Завантажуємо збережені значення місяця та року з localStorage (тільки якщо немає в URL)
    const userId = searchParams.get('userId');
    const month = searchParams.get('month');
    const year = searchParams.get('year');
    
    // Якщо в URL немає параметрів місяця та року, завантажуємо з localStorage
    if (!month && !year) {
      const savedMonth = localStorage.getItem('selectedMonth');
      const savedYear = localStorage.getItem('selectedYear');
      
      if (savedMonth) {
        setSelectedMonth(savedMonth);
      }
      if (savedYear) {
        setSelectedYear(parseInt(savedYear));
      }
    }
  }, []);

  // Обробка URL параметрів
  useEffect(() => {
    const userId = searchParams.get('userId');
    const month = searchParams.get('month');
    const year = searchParams.get('year');

    if (month) {
      setSelectedMonth(month);
    }

    if (year) {
      setSelectedYear(year);
    }

    // Якщо є userId, завантажуємо дані водія
    if (userId) {
      const loadDriver = async () => {
        try {
          const response = await axios.get(`/api/users/${userId}`);
          const driverData = response.data;
          setSelectedDriver({
            value: driverData.id,
            label: `${driverData.firstName} ${driverData.lastName}`
          });
        } catch (error) {
          console.error('Помилка завантаження даних водія:', error);
        }
      };
      loadDriver();
    }
  }, [searchParams]);

  // Зберігаємо вибрані значення в localStorage при зміні (тільки якщо немає в URL)
  useEffect(() => {
    const month = searchParams.get('month');
    const year = searchParams.get('year');
    
    // Зберігаємо тільки якщо значення не прийшли з URL
    if (!month && selectedMonth) {
      localStorage.setItem('selectedMonth', selectedMonth);
    }
  }, [selectedMonth, searchParams]);

  useEffect(() => {
    const year = searchParams.get('year');
    
    // Зберігаємо тільки якщо значення не прийшло з URL
    if (!year && selectedYear) {
      localStorage.setItem('selectedYear', selectedYear.toString());
    }
  }, [selectedYear, searchParams]);

  // Автоматичне завантаження даних після встановлення параметрів з URL
  useEffect(() => {
    const userId = searchParams.get('userId');
    const month = searchParams.get('month');
    const year = searchParams.get('year');

    // Якщо всі параметри є в URL і всі стани встановлені
    if (userId && month && year && selectedDriver && selectedMonth && selectedYear) {
      // Запускаємо завантаження даних
      fetchDriverReports();
    }
  }, [selectedDriver, selectedMonth, selectedYear]);

  // Валідація полів
  const validateFields = () => {
    if (!selectedDriver) {
      setValidationError(t('pleaseSelectDriver'));
      return false;
    }
    if (!selectedMonth) {
      setValidationError(t('pleaseSelectMonth'));
      return false;
    }
    if (!selectedYear) {
      setValidationError(t('pleaseSelectYear'));
      return false;
    }
    setValidationError(null);
    return true;
  };

  // Перевірка існуючих рахунків
  const checkExistingInvoices = async () => {
    if (!selectedDriver || !selectedMonth || !selectedYear) {
      return;
    }

    try {
      const response = await axios.get(`/api/invoices/check/${selectedDriver.value}/${selectedMonth}/${selectedYear}`);
      setExistingInvoices(response.data);
    } catch (error) {
      console.error('Помилка перевірки існуючих рахунків:', error);
      setExistingInvoices(null);
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    if (!invoice?.id) {
      console.error('Не вдалося визначити ID рахунку для видалення', invoice);
      alert(t('deleteInvoiceMissingId'));
      return;
    }

    const confirmed = window.confirm(t('confirmDeleteInvoice', { number: invoice.invoice_number }));

    if (!confirmed) {
      return;
    }

    setDeletingInvoices((prev) => ({
      ...prev,
      [invoice.id]: true
    }));

    try {
      await axios.delete(`/api/invoices/${invoice.id}`);
      alert(t('deleteInvoiceSuccess', { number: invoice.invoice_number }));
      await checkExistingInvoices();
    } catch (error) {
      console.error('Помилка видалення рахунку:', error);
      alert(t('deleteInvoiceError', { error: error.response?.data?.error || error.message }));
    } finally {
      setDeletingInvoices((prev) => {
        const updated = { ...prev };
        delete updated[invoice.id];
        return updated;
      });
    }
  };

  // Перехід до генератора рахунків
  const goToInvoiceGenerator = () => {
    if (!validateFields()) {
      return;
    }
    
    const params = new URLSearchParams({
      driverId: selectedDriver.value,
      month: selectedMonth,
      year: selectedYear
    });
    
    navigate(`/admin/generate-invoice?${params.toString()}`);
  };

  // Завантаження рапортів водія
  const fetchDriverReports = async () => {
    if (!validateFields()) {
      return;
    }

    setLoadingReports(true);
    setError(null);
    setReportsData(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`/api/monthly-reports/driver/${selectedDriver.value}`, {
        params: {
          month: selectedMonth,
          year: selectedYear
        },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      setReportsData(response.data);
      console.log('Завантажені рапорти:', response.data);
      
      // Перевіряємо існуючі рахунки після завантаження рапортів
      await checkExistingInvoices();
    } catch (err) {
      console.error('Помилка завантаження рапортів:', err);
      setError(t('errorLoadingReports'));
    } finally {
      setLoadingReports(false);
    }
  };

  // Отримання даних водія з БД
  const fetchDriverData = async (driverId) => {
    try {
      const response = await axios.get(`/api/users/${driverId}`);
      return response.data;
    } catch (error) {
      console.error('Помилка отримання даних водія:', error);
      return null;
    }
  };

  // Збереження даних рахунку
  const saveInvoiceData = async () => {
    if (!validateFields() || !reportsData) {
      console.log('Помилка: немає даних для збереження');
      return;
    }

    // Додаткова перевірка структури даних
    if (!reportsData.totals) {
      console.log('Помилка: totals не знайдено в reportsData');
      console.log('reportsData:', reportsData);
      return;
    }

    // Отримуємо дані водія
    const driverData = await fetchDriverData(selectedDriver.value);
    if (!driverData) {
      alert('Помилка отримання даних водія');
      return;
    }

    // Отримуємо існуючі рахунки для цього водія за місяць
    let existingInvoices = [];
    try {
      const response = await axios.get(`/api/invoices/check/${selectedDriver.value}/${selectedMonth}/${selectedYear}`);
      existingInvoices = response.data.invoices || [];
    } catch (error) {
      console.error('Помилка отримання існуючих рахунків:', error);
    }

    // Підготовка даних для збереження
    const transferWeeks = reportsData.totals?.transferWeeks || [];
    const cashWeeks = reportsData.totals?.cashWeeks || [];
    
    const invoiceData = {
      driverId: selectedDriver.value,
      month: selectedMonth,
      year: selectedYear,
      monthLabel: months.find(m => m.value === selectedMonth)?.label,
      weeks: reportsData.weeks || [],
      reports: reportsData.reports || [],
      totals: reportsData.totals || {},
      // Розділяємо дані на transfer та cash
      transferData: {
        weeks: transferWeeks,
        totalAmount: reportsData.totals?.totalTransfer || 0,
        count: transferWeeks.length
      },
      cashData: {
        weeks: cashWeeks,
        totalAmount: reportsData.totals?.totalCash || 0,
        count: cashWeeks.length
      }
    };

    // Створюємо JSON об'єкти для transfer та cash
    const transferWeeksData = [];
    const cashWeeksData = [];

    // Обробляємо кожен рапорт
    invoiceData.reports.forEach((report) => {
      const weekNumber = report.week_number;
      const reportJson = report.report_json || {};
      
      // Спочатку перевіряємо готівку - вона має додаватися незалежно від payout
      // Для cash - сумуємо incNettoCash, incCash та cash
      let cashTotal = 0;
      
      // Перевіряємо всі сервіси (bolt, uber, freenow)
      ['bolt', 'uber', 'freenow'].forEach(service => {
        if (reportJson.report && reportJson.report[service]) {
          reportJson.report[service].forEach((serviceReport, index) => {
            const incNettoCash = parseFloat(serviceReport.incNettoCash || 0);
            const incCash = Math.abs(parseFloat(serviceReport.incCash || 0)); // Беремо модуль для incCash
            const cash = parseFloat(serviceReport.cash || 0);
            
            cashTotal += incNettoCash + incCash + cash;
          });
        }
      });

      // Додаємо готівку до рахунку, якщо вона є
      if (cashTotal > 0) {
        cashWeeksData.push({
          week: weekNumber,
          amount: Math.round(cashTotal * 100) / 100, // Округлюємо до 2 знаків
          type: 'cash',
          breakdown: {
            incNettoCash: reportJson.report ? 
              Math.round(Object.values(reportJson.report).flat().reduce((sum, item) => sum + parseFloat(item.incNettoCash || 0), 0) * 100) / 100 : 0,
            incCash: reportJson.report ? 
              Math.round(Object.values(reportJson.report).flat().reduce((sum, item) => sum + Math.abs(parseFloat(item.incCash || 0)), 0) * 100) / 100 : 0,
            cash: reportJson.report ? 
              Math.round(Object.values(reportJson.report).flat().reduce((sum, item) => sum + parseFloat(item.cash || 0), 0) * 100) / 100 : 0
          }
        });
      }
      
      // Для transfer - використовуємо payout (всі тижні, де payout > 0)
      const payout = parseFloat(reportJson.payout || 0);
      if (payout > 0) {
        transferWeeksData.push({
          week: weekNumber,
          amount: Math.round(payout * 100) / 100, // Округлюємо до 2 знаків
          type: 'payout'
        });
      }
    });

    // Знаходимо існуючі номери рахунків для цього водія
    const existingTransferInvoice = existingInvoices.find(inv => inv.payment_method === 'transfer');
    const existingCashInvoice = existingInvoices.find(inv => inv.payment_method === 'cash');
    
    // Створюємо об'єкти для збереження в БД
    // Номери рахунків генеруються на сервері
    const invoicesToSave = [];
    
    // 1. Transfer рахунок (створюємо якщо є дані)
    if (transferWeeksData.length > 0) {
      // Номер буде згенерований на сервері для нових рахунків
      let transferInvoiceNumber;
      if (existingTransferInvoice) {
        // Якщо рахунок вже існує - використовуємо його номер для оновлення
        transferInvoiceNumber = existingTransferInvoice.invoice_number;
      }
      // Якщо рахунок не існує - не передаємо invoice_number, сервер згенерує
        
      const transferInvoice = {
        ...(transferInvoiceNumber && { invoice_number: transferInvoiceNumber }),
        driver_id: parseInt(invoiceData.driverId),
        month: invoiceData.month,
        year: parseInt(invoiceData.year),
        weeks: JSON.stringify(transferWeeksData),
        seller_data: JSON.stringify({
          name: `${driverData.firstName} ${driverData.lastName}`,
          address: `${driverData.street || ''} ${driverData.house_number || ''}`.trim(),
          city: `${driverData.index || ''}, ${driverData.city || ''}`.replace(/^,\s*/, ''),
          pesel: driverData.pesel || ''
        }),
        buyer_data: JSON.stringify({
          name: "TORI Viktoriya Manachynska",
          address: "ul. Księcia Witolda 49/15",
          city: "50-202 Wrocław", 
          nip: "7822599564"
        }),
        invoice_data: JSON.stringify({
          total: Math.round(transferWeeksData.reduce((sum, item) => sum + item.amount, 0) * 100) / 100
        }),
        payment_method: 'transfer',
        payment_due_date: new Date().toISOString().split('T')[0],
        bank_account: driverData.account_number || '',
        status: 'draft',
        created_by: 1 // ID адміністратора
      };
      invoicesToSave.push(transferInvoice);
    }
    
    // 2. Cash рахунок (створюємо тільки якщо є cash дані)
    if (cashWeeksData.length > 0) {
      // Перевіряємо, чи є готівка за будь-який з тижнів
      const hasCashData = cashWeeksData.some(week => week.amount > 0);
      
        if (hasCashData) {
          // Номер буде згенерований на сервері для нових рахунків
          let cashInvoiceNumber;
          if (existingCashInvoice) {
            // Якщо рахунок вже існує - використовуємо його номер для оновлення
            cashInvoiceNumber = existingCashInvoice.invoice_number;
          }
          // Якщо рахунок не існує - не передаємо invoice_number, сервер згенерує
          
          const cashInvoice = {
            ...(cashInvoiceNumber && { invoice_number: cashInvoiceNumber }),
          driver_id: parseInt(invoiceData.driverId),
          month: invoiceData.month,
          year: parseInt(invoiceData.year),
          weeks: JSON.stringify(cashWeeksData),
          seller_data: JSON.stringify({
            name: `${driverData.firstName} ${driverData.lastName}`,
            address: `${driverData.street || ''} ${driverData.house_number || ''}`.trim(),
            city: `${driverData.index || ''}, ${driverData.city || ''}`.replace(/^,\s*/, ''),
            pesel: driverData.pesel || ''
          }),
          buyer_data: JSON.stringify({
            name: "TORI Viktoriya Manachynska",
            address: "ul. Księcia Witolda 49/15",
            city: "50-202 Wrocław",
            nip: "7822599564"
          }),
          invoice_data: JSON.stringify({
            total: Math.round(cashWeeksData.reduce((sum, item) => sum + item.amount, 0) * 100) / 100
          }),
          payment_method: 'cash',
          payment_due_date: new Date().toISOString().split('T')[0],
          bank_account: driverData.account_number || '',
          status: 'draft',
          created_by: 1 // ID адміністратора
        };
        invoicesToSave.push(cashInvoice);
      }
    }

    // Зберігаємо/оновлюємо рахунки в БД
    setSavingInvoices(true);
    try {
      const results = [];
      for (const invoice of invoicesToSave) {
        // Використовуємо upsert endpoint для створення або оновлення рахунку
        const response = await axios.put('/api/invoices/upsert', invoice);
        results.push(response.data);
      }
      
      // Підраховуємо створені та оновлені рахунки
      const created = results.filter(r => r.action === 'created').length;
      const updated = results.filter(r => r.action === 'updated').length;
      
      let message = '';
      if (created > 0 && updated > 0) {
        message = `Успішно створено ${created} та оновлено ${updated} рахунків!`;
      } else if (created > 0) {
        message = `Успішно створено ${created} рахунків!`;
      } else if (updated > 0) {
        message = `Успішно оновлено ${updated} рахунків!`;
      }
      
      
      alert(message);
      
      // Оновлюємо дані та перевіряємо існуючі рахунки
      await checkExistingInvoices();
      
    } catch (error) {
      console.error('Помилка збереження рахунків:', error);
      alert('Помилка збереження рахунків: ' + (error.response?.data?.error || error.message));
    } finally {
      setSavingInvoices(false);
    }
  };


  return (
    <div className="container">
      <h2>📅 {t("monthlyReports")}</h2>

      <div className="row mb-4">
        <div className="col-md-3">
          <label className="form-label fw-bold">{t("chooseDriver")}</label>
          <DriverSelector 
            selectedDriver={selectedDriver} 
            setSelectedDriver={setSelectedDriver} 
          />
        </div>
        <div className="col-md-3">
          <label className="form-label fw-bold">{t("selectMonth")}</label>
          <Form.Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="">{t("selectMonth")}</option>
            {months.map(month => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-3">
          <label className="form-label fw-bold">{t("year")}</label>
          <Form.Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {years.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-3">
          <label className="form-label fw-bold">&nbsp;</label>
          <div>
            <Button 
              variant="primary" 
              onClick={fetchDriverReports}
              disabled={loadingReports || loading}
              className="w-100"
            >
              {loadingReports ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("loading")}...
                </>
              ) : (
                `📊 ${t("loaddata")}`
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Список тижнів для обраного місяця */}
      {!loading && !error && monthsData && selectedMonth && selectedYear && (
        <div className="mt-3">
          <h5 className="mb-3">📋 {t('weeksFor')} {months.find(m => m.value === selectedMonth)?.label} {selectedYear}</h5>
          <div className="row">
            {getWeeksForSelectedMonth().map(week => (
              <div key={week} className="col-md-3 col-sm-4 col-6 mb-2">
                <div className="card border-primary" style={{ height: '80px' }}>
                  <div className="card-body text-center d-flex flex-column justify-content-center p-2">
                    <h6 className="card-title text-primary mb-1" style={{ fontSize: '0.9rem' }}>{t('week')} {week}</h6>
                    <p className="card-text small text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                      {t("week")} #{week}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {getWeeksForSelectedMonth().length === 0 && (
            <Alert variant="warning" className="mt-3">
              {t('weeksNotFoundForMonth')}
            </Alert>
          )}
        </div>
      )}

      {/* Завантаження даних */}
      {loading && (
        <div className="text-center my-4">
          <Spinner animation="border" />
          <p className="mt-2">{t("loading")}...</p>
        </div>
      )}

      {/* Помилка валідації */}
      {validationError && (
        <Alert variant="warning" className="mt-4">
          <Alert.Heading>⚠️ Увага!</Alert.Heading>
          {validationError}
        </Alert>
      )}

      {/* Помилка завантаження */}
      {error && (
        <Alert variant="danger" className="mt-4">
          <Alert.Heading>{t('errorOccurred')}!</Alert.Heading>
          {error}
          <hr />
          <button className="btn btn-outline-danger" onClick={fetchMonthsData}>
            {t('tryAgain')}
          </button>
        </Alert>
      )}

      {/* Результати завантаження рапортів */}
      {reportsData && (
        <div className="mt-4">
          <Alert variant="success">
            <Alert.Heading>✅ {t('reportsLoaded')}</Alert.Heading>
            <p>
              {t('foundReports')} <strong>{reportsData.reportCount}</strong> {t('reportsForDriver')}{' '}
              <strong>{selectedDriver?.label}</strong> {t('for')}{' '}
              <strong>{months.find(m => m.value === selectedMonth)?.label} {selectedYear}</strong>
            </p>
          </Alert>

          {/* Інформація про існуючі рахунки */}
          {existingInvoices && existingInvoices.count > 0 && (
            <div className="mt-4">
              <Alert variant="warning">
                <Alert.Heading>⚠️ {t('existingInvoices')}</Alert.Heading>
                <p>{t('existingInvoicesForDriverMonth')}</p>
                <ul className="mb-3">
                  {existingInvoices.invoices.map((invoice, index) => {
                    const key = invoice.id ?? `${invoice.invoice_number}-${index}`;
                    const deletable = isInvoiceDeletable(invoice.status);
                    const isDeleting = !!(invoice.id && deletingInvoices[invoice.id]);

                    return (
                      <li key={key} className="mb-2">
                        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start gap-2">
                          <div>
                            <strong>{t('invoice')} #{invoice.invoice_number}</strong> ({invoice.payment_method === 'transfer' ? t('transfer') : t('cash')}) - 
                            {t('invoiceStatus')}: <span className={`badge bg-${invoice.status === 'draft' ? 'secondary' : invoice.status === 'saved' ? 'info' : invoice.status === 'sent' ? 'warning' : invoice.status === 'published' ? 'success' : 'secondary'}`}>
                              {invoice.status === 'draft'
                                ? t('draft')
                                : invoice.status === 'saved'
                                  ? t('saved')
                                  : invoice.status === 'sent'
                                    ? t('sent')
                                    : invoice.status === 'published'
                                      ? t('published')
                                      : invoice.status}
                            </span>
                            <br />
                            <small className="text-muted">
                              {t('invoiceNumber')}: {invoice.invoice_number} | 
                              {t('invoiceCreated')}: {new Date(invoice.created_at).toLocaleString('uk-UA')}
                              {invoice.updated_at !== invoice.created_at && (
                                <span> | {t('invoiceUpdated')}: {new Date(invoice.updated_at).toLocaleString('uk-UA')}</span>
                              )}
                            </small>
                          </div>
                          {deletable && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="d-flex align-items-center gap-2"
                              onClick={() => handleDeleteInvoice(invoice)}
                              disabled={isDeleting}
                            >
                              {isDeleting ? (
                                <>
                                  <Spinner animation="border" size="sm" />
                                  {t('deleteInvoice')}
                                </>
                              ) : (
                                <>🗑️ {t('deleteInvoice')}</>
                              )}
                            </Button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <p className="mb-3 text-danger"><strong>⚠️ {t('warning')}:</strong> {t('warningInvoiceOverwrite')}</p>
                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={goToInvoiceGenerator}
                  >
                    👁️ {t('viewInvoices')}
                  </Button>
                </div>
              </Alert>
            </div>
          )}

          {/* Таблиця з даними по тижнях */}
          <div className="mt-4">
            <h5>📊 {t('detailedDataByWeeks')}</h5>
            <Table striped bordered hover responsive>
              <thead className="table-dark">
                <tr>
                  <th>{t('week')}</th>
                  <th>{t('payout')}</th>
                  <th>Готівка</th>
                  <th colSpan="2">Bolt</th>
                  <th colSpan="2">Uber</th>
                  <th colSpan="2">FreeNow</th>
                  <th colSpan="4">{t('additionalExpenses')}</th>
                </tr>
                <tr>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th>Нетто</th>
                  <th>Готівка</th>
                  <th>Нетто</th>
                  <th>Готівка</th>
                  <th>Нетто</th>
                  <th>Готівка</th>
                  <th>{t('partner')}</th>
                  <th>{t('sallary')}</th>
                  <th>{t('extraFunds.taxesZUS')}</th>
                  <th>{t('accountant')}</th>
                </tr>
              </thead>
              <tbody>
                {reportsData.reports.map(report => {
                  const boltData = report.report_json?.report?.bolt?.[0] || {};
                  const uberData = report.report_json?.report?.uber?.[0] || {};
                  const freenowData = report.report_json?.report?.freenow?.[0] || {};
                  const extraFunds = report.report_json?.extraFunds || {};
                  const weekCash = parseFloat(boltData.incNettoCash || 0) + Math.abs(parseFloat(uberData.incCash || 0)) + parseFloat(freenowData.cash || 0);

                  return (
                    <tr key={report.id}>
                      <td><strong>{report.week_number}</strong></td>
                      <td><strong>{parseFloat(report.report_json?.payout || 0).toFixed(2)}</strong></td>
                      <td><strong>{weekCash.toFixed(2)}</strong></td>
                      <td>{parseFloat(boltData.incNetto || 0).toFixed(2)}</td>
                      <td>{parseFloat(boltData.incNettoCash || 0).toFixed(2)}</td>
                      <td>{parseFloat(uberData.incNetto || 0).toFixed(2)}</td>
                      <td>{Math.abs(parseFloat(uberData.incCash || 0)).toFixed(2)}</td>
                      <td>{parseFloat(freenowData.netto || 0).toFixed(2)}</td>
                      <td>{parseFloat(freenowData.cash || 0).toFixed(2)}</td>
                      <td>{parseFloat(extraFunds.partner || 0).toFixed(2)}</td>
                      <td>{parseFloat(extraFunds.sallary || 0).toFixed(2)}</td>
                      <td>{parseFloat(extraFunds.taxesZUS || 0).toFixed(2)}</td>
                      <td>{parseFloat(extraFunds.accountant || 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
                
                {/* Рядок з підсумками */}
                <tr className="table-warning fw-bold">
                  <td><strong>{t('summary')}</strong></td>
                  <td>
                    {reportsData.reports.reduce((sum, report) => {
                      return sum + parseFloat(report.report_json?.payout || 0);
                    }, 0).toFixed(2)}
                  </td>
                  <td>
                    {reportsData.reports.reduce((sum, report) => {
                      const boltData = report.report_json?.report?.bolt?.[0] || {};
                      const uberData = report.report_json?.report?.uber?.[0] || {};
                      const freenowData = report.report_json?.report?.freenow?.[0] || {};
                      return sum + parseFloat(boltData.incNettoCash || 0) + Math.abs(parseFloat(uberData.incCash || 0)) + parseFloat(freenowData.cash || 0);
                    }, 0).toFixed(2)}
                  </td>
                  <td>
                    {reportsData.reports.reduce((sum, report) => {
                      const boltData = report.report_json?.report?.bolt?.[0] || {};
                      return sum + parseFloat(boltData.incNetto || 0);
                    }, 0).toFixed(2)}
                  </td>
                  <td>
                    {reportsData.reports.reduce((sum, report) => {
                      const boltData = report.report_json?.report?.bolt?.[0] || {};
                      return sum + parseFloat(boltData.incNettoCash || 0);
                    }, 0).toFixed(2)}
                  </td>
                  <td>
                    {reportsData.reports.reduce((sum, report) => {
                      const uberData = report.report_json?.report?.uber?.[0] || {};
                      return sum + parseFloat(uberData.incNetto || 0);
                    }, 0).toFixed(2)}
                  </td>
                  <td>
                    {reportsData.reports.reduce((sum, report) => {
                      const uberData = report.report_json?.report?.uber?.[0] || {};
                      return sum + Math.abs(parseFloat(uberData.incCash || 0));
                    }, 0).toFixed(2)}
                  </td>
                  <td>
                    {reportsData.reports.reduce((sum, report) => {
                      const freenowData = report.report_json?.report?.freenow?.[0] || {};
                      return sum + parseFloat(freenowData.netto || 0);
                    }, 0).toFixed(2)}
                  </td>
                  <td>
                    {reportsData.reports.reduce((sum, report) => {
                      const freenowData = report.report_json?.report?.freenow?.[0] || {};
                      return sum + parseFloat(freenowData.cash || 0);
                    }, 0).toFixed(2)}
                  </td>
                  <td>
                    {reportsData.reports.reduce((sum, report) => {
                      const extraFunds = report.report_json?.extraFunds || {};
                      return sum + parseFloat(extraFunds.partner || 0);
                    }, 0).toFixed(2)}
                  </td>
                  <td>
                    {reportsData.reports.reduce((sum, report) => {
                      const extraFunds = report.report_json?.extraFunds || {};
                      return sum + parseFloat(extraFunds.sallary || 0);
                    }, 0).toFixed(2)}
                  </td>
                  <td>
                    {reportsData.reports.reduce((sum, report) => {
                      const extraFunds = report.report_json?.extraFunds || {};
                      return sum + parseFloat(extraFunds.taxesZUS || 0);
                    }, 0).toFixed(2)}
                  </td>
                  <td>
                    {reportsData.reports.reduce((sum, report) => {
                      const extraFunds = report.report_json?.extraFunds || {};
                      return sum + parseFloat(extraFunds.accountant || 0);
                    }, 0).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>

          {/* Кнопка збереження даних */}
          <div className="mt-4 text-center">
            <Button 
              variant="primary" 
              size="lg"
              onClick={saveInvoiceData}
              disabled={savingInvoices}
            >
              {savingInvoices ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t('saving')}
                </>
              ) : (
                `💾 ${t('saveData')}`
              )}
            </Button>
          </div>
        </div>
      )}


      {/* Повідомлення про вибір місяця */}
      {!loading && !error && monthsData && (!selectedMonth || !selectedYear) && (
        <Alert variant="info" className="mt-4">
          <Alert.Heading>{t('selectMonthAndYear')}</Alert.Heading>
          {t('selectMonthAndYearMessage')}
        </Alert>
      )}
      

    </div>
  );
};

export default MonthlyReports;
